import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { ChatMemberStatusUnion, ChatMemberUnion, ChatPermissionsUnion } from '@airgram/web';
import { NgxPermissionsService } from 'ngx-permissions';
import { TranslateService } from '@ngx-translate/core';
import { TelegramMessengerService } from '@src/app/modules/telegram';
import { EnvService } from '@src/app/modules/env';
import { ChatMemberModel, ChatModel, InviteUserUI, UserUI } from '@src/models';
import { InvitesService, UserService } from '@src/core/services';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { getImageSrc } from '@src/utils';
import { InviteView } from '@src/api';
import { SearchUsersService } from '@src/app/modules/search-users';
import { BaseResizableComponent } from '@src/app/modules/base';

import { GroupInfoService } from '../group-info';

@Component({
  selector: 'app-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersListComponent extends BaseResizableComponent implements OnChanges {
  public readonly env = inject(EnvService);
  private readonly messengerService = inject(TelegramMessengerService);
  private readonly userService = inject(UserService);
  private readonly groupInfoService = inject(GroupInfoService);
  private readonly invitesService = inject(InvitesService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);
  private readonly translateService = inject(TranslateService);
  private readonly searchUsersService = inject(SearchUsersService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);

  @Input() isChannel?: boolean;
  @Input() memberCount?: number;
  @Input() chatId?: number;
  @Input() organisationId?: string;
  @Input() members?: ChatMemberUnion[];
  @Output() changeMembers = new EventEmitter<void>();
  @Output() selectedUserIdChange = new EventEmitter<string>();

  membersTelegramIds?: number[];
  newMembers?: UserUI[] | null;
  authUser?: UserUI;
  currentUserChatStatus?: ChatMemberStatusUnion;
  chatPermissions?: ChatPermissionsUnion;
  chat?: ChatModel;
  inviteUsers: InviteUserUI[] = [];
  inviteUsersIds: string[] = [];
  telegramUsers: ChatMemberModel[] = [];
  unionsUsers: ChatMemberModel[] = [];

  loading = false;

  private loadChat$ = new BehaviorSubject<number | null>(null);
  private loadInvitesByChatId$ = new BehaviorSubject<number | null>(null);
  private readonly confirmDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('components.usersList.dialogs.memberDeleteHeader'),
      size: 's',
      closeable: false,
    },
  );

  constructor() {
    super();

    this.userService.getAuthorizedUser().then(user => {
      this.authUser = user;
      this.getUserChatPermissions();
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.userService
      .getUsersObservable()
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(response => {
        if (!!response) {
          (this.membersTelegramIds || []).map(async memberId => {
            const telegramUser = response.telegramUsers?.find(telegramUser => telegramUser.id === memberId);
            if (!!telegramUser) {
              const unionsUser = (response.users || []).find(unionsUser => unionsUser.telegramId === memberId);
              if (!!unionsUser) {
                telegramUser.lastName = unionsUser.lastName ? unionsUser.lastName : '';
                telegramUser.firstName = unionsUser.firstName ? unionsUser.firstName : '';
                telegramUser.middleName = unionsUser.middleName ? unionsUser.middleName : '';
                if (unionsUser.photoId) {
                  telegramUser.photoPath = getImageSrc(unionsUser.photoId);
                }
                telegramUser.unionsId = unionsUser.id;

                const defaultOrganisationJobTitle = unionsUser.organisationJobTitles?.find(
                  jobTitle => jobTitle.organisationId === unionsUser.organisationId,
                );
                if (defaultOrganisationJobTitle) {
                  telegramUser.organisationJobTitles = [defaultOrganisationJobTitle];
                }

                this.unionsUsers.push(telegramUser);
              } else {
                this.telegramUsers.push(telegramUser);
              }
            }
          });
        }

        this.unionsUsers = this.unionsUsers.filter(unionsUser => !!unionsUser);
        this.unionsUsers = this.sortingMembers(this.unionsUsers) as ChatMemberModel[];

        this.telegramUsers = this.telegramUsers.filter(telegramUser => !!telegramUser);
        this.telegramUsers = this.sortingMembers(this.telegramUsers) as ChatMemberModel[];

        this.cdr.markForCheck();

        this.loadChat$
          .pipe(takeUntil(this.destroyed$$))
          .pipe(filter(chatId => chatId !== null))
          .pipe(
            switchMap(chatId => {
              if (!!chatId) {
                return this.messengerService.api.getChatObservable(chatId);
              } else {
                return of(null);
              }
            }),
          )
          .pipe(
            switchMap(chat => {
              if (!!chat) {
                this.chat = chat;
                this.chatPermissions = chat.permissions;
                return this.messengerService.api.getChatMemberObservable(this.chatId!, {
                  _: 'messageSenderUser',
                  userId: this.authUser!.telegramId,
                });
              } else {
                return of(null);
              }
            }),
          )
          .subscribe(chatMember => {
            this.currentUserChatStatus = chatMember?.status;
            if (!!this.unionsUsers.length) {
              this.getInviteMembers();
            }
            this.cdr.markForCheck();
          });
      });

    let invitesSaved: InviteView[] = [];
    this.loadInvitesByChatId$
      .pipe(takeUntil(this.destroyed$$))
      .pipe(filter(chatId => chatId !== null))
      .pipe(
        switchMap(chatId => {
          invitesSaved = [];
          if (!!chatId) {
            return this.invitesService.getInvitesByChatId(chatId);
          } else {
            return of(null);
          }
        }),
      )
      .pipe(
        switchMap(invites => {
          if (!!invites) {
            invitesSaved = invites;
            this.inviteUsersIds = invites
              .map(inviteMember => inviteMember.userId)
              .filter(id => id !== undefined && !this.unionsUsers.find(user => user.unionsId === id)) as string[];

            return this.userService.getUsersData(this.inviteUsersIds);
          } else {
            return of(null);
          }
        }),
      )
      .subscribe(unionsUsers => {
        const inviteUsers = (unionsUsers as InviteUserUI[]).map(unionsUser => {
          const findInvite = invitesSaved.find(invite => invite.userId === unionsUser.id);
          if (findInvite) {
            unionsUser.inviteStatus = findInvite.inviteStatus;
            if (unionsUser.photoId) {
              unionsUser.photoPath = getImageSrc(unionsUser.photoId);
            }
          }

          return unionsUser;
        });

        this.inviteUsers = this.sortingMembers(inviteUsers) as InviteUserUI[];

        this.cdr.markForCheck();
      });
  }

  getMembersTelegramIds(chatMembers: ChatMemberUnion[]): number[] {
    return chatMembers.map(member => {
      if (member.memberId._ === 'messageSenderUser') {
        return member.memberId.userId;
      }
      if (member.memberId._ === 'messageSenderChat') {
        return member.memberId.chatId;
      }
      return;
    }) as number[];
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.members) {
      if (this.members) {
        this.membersTelegramIds = this.getMembersTelegramIds(this.members);
        const previousMembersTelegramIds = !changes.members.firstChange
          ? this.getMembersTelegramIds(changes.members.previousValue)
          : this.membersTelegramIds;

        if (
          changes.members.firstChange ||
          JSON.stringify(this.membersTelegramIds) !== JSON.stringify(previousMembersTelegramIds)
        ) {
          this.unionsUsers = [];
          this.telegramUsers = [];
          this.userService.loadUsers(this.membersTelegramIds);
        }
      }
    }

    if (changes.chatId) {
      if (changes.chatId.firstChange || changes.chatId.currentValue !== changes.chatId.previousValue) {
        this.getUserChatPermissions();
      }
    }

    this.cdr.markForCheck();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.userService.loadUsers(null);
    this.loadInvitesByChatId$.next(null);
    this.loadChat$.next(null);
  }

  onSelectedIdChangeUnionsUsers(id?: string | number) {
    if (typeof id === 'number') {
      id = this.unionsUsers?.find(user => user.id === id)?.unionsId;
    }

    if (typeof id === 'string') {
      this.selectedUserIdChange.emit(id);
    }
  }

  onSelectedIdChangeInviteUsers(id?: string | number) {
    if (typeof id === 'number') {
      id = this.inviteUsers?.find(user => user.telegramId === id)?.id;
    }

    if (typeof id === 'string') {
      this.selectedUserIdChange.emit(id);
    }
  }

  onClickDeleteMemberButton($event: MouseEvent, userId: number) {
    $event.stopPropagation();
    if (!this.chatId || !userId) return;

    this.confirmDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        if (res) {
          this.loading = true;
          this.messengerService.api.deleteChatMember(this.chatId!, { _: 'messageSenderUser', userId }).then(() => {
            this.changeMembers.emit();
            this.loading = false;
            this.cdr.markForCheck();
          });

          this.cdr.markForCheck();
        }
      },
    });
  }

  openSearchMembersDialog() {
    const filter = { searchForRegistredOnly: this.organisationId ? undefined : true };

    this.searchUsersService
      .searchMultiple(undefined, undefined, this.inviteUsersIds, this.membersTelegramIds, filter)
      .subscribe(members => {
        if (!this.chat || !members || !members.length) return;
        this.loading = true;

        this.groupInfoService.addMembers(this.chat, members, this.organisationId).then(() => {
          this.getInviteMembers();
          this.changeMembers.emit();
          this.loading = false;
          this.cdr.markForCheck();
        });

        this.cdr.markForCheck();
      });
  }

  private getUserChatPermissions(): void {
    if (!this.chatId || !this.authUser?.telegramId) return;

    this.loadChat$.next(this.chatId);
  }

  private getInviteMembers() {
    const permissions = this.ngxPermissionsService.getPermissions();
    if (
      this.chatId &&
      ('chatInviteMembersViewing' in permissions || this.currentUserChatStatus?._ === 'chatMemberStatusCreator')
    ) {
      this.loadInvitesByChatId$.next(this.chatId);
    }
  }

  private sortingMembers(chatMembers: (ChatMemberModel | InviteUserUI)[]): (ChatMemberModel | InviteUserUI)[] {
    chatMembers.sort((member1, member2) => {
      const memberFullName1 = (
        (member1.lastName ?? '') +
        (member1.firstName ?? '') +
        (member1.middleName ?? '')
      ).toLowerCase();
      const memberFullName2 = (
        (member2.lastName ?? '') +
        (member2.firstName ?? '') +
        (member2.middleName ?? '')
      ).toLowerCase();

      if (!memberFullName1) return 1;
      if (!memberFullName2) return -1;

      if (memberFullName1 < memberFullName2) return -1;
      if (memberFullName1 > memberFullName2) return 1;
      return 0;
    });

    return chatMembers;
  }
}
